import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import permissionsAPI from "../../../api/permissionsApi";
import SubmitButton from "../../../components/partials/global/SubmitButton";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";

function Add() {
  const location = useLocation();
  const { name } = location.state || {};
  const { id } = useParams();

  const [modules, setModules] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [roleCode, setRoleCode] = useState("STOREADMIN");
  const [errors, setErrors] = useState({});
  
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);

  const ACTIONS = ["READ", "CREATE", "UPDATE", "DELETE"];

  /* ============= LOAD DATA ============== */
  const loadModules = async () => {
    try {
      const res = await permissionsAPI.getDetails();
      if (res.code === 200) {
        setModules(res.data);
      }
    } catch (error) {
      showError("Failed to load modules");
    }
  };

  const loadDetails = async () => {
    try {
      const res = await permissionsAPI.getSingleDetails(id);
      if (res.code === 200) {
        setSelectedPermissions(res.data.permissions);
        setRoleCode(res.data.code);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    loadModules();
    if (id) {
      loadDetails();
    }
  }, [id]);

  /* ============= SELECT DATA ============== */
  const togglePermission = (module, action) => {
    const key = `${action}_${module}`;

    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const toggleModuleAll = (module, checked) => {
    const modulePermissions = ACTIONS.map((a) => `${a}_${module}`);

    setSelectedPermissions((prev) => {
      if (checked) {
        return [...new Set([...prev, ...modulePermissions])];
      } else {
        return prev.filter((p) => !p.endsWith("_" + module));
      }
    });
  };

  const toggleGlobalAll = (checked) => {
    if (!checked) return setSelectedPermissions([]);

    const all = [];
    modules.forEach((m) => {
      ACTIONS.forEach((a) => {
        all.push(`${a}_${m}`);
      });
    });

    setSelectedPermissions(all);
  };

  /* ============= SUBMIT FORM ============== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader();
    setIsLoading(true);

    try {
      const payload = {
        userId: id,
        code: roleCode,
        resource: selectedPermissions,
      };

      const res = await permissionsAPI.addDetails(payload);

      if (res.code === 200) {
        showSuccess(res.message || "Permissions updated successfully");
      } else {
        showError(res.message || "Something went wrong");
      }
    } catch (err) {
      showError("Server error occurred");
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <div className="content">
      <div className="page-header">
        <div className="page-title">
          <h4>Permissions</h4>
          <h6>Ensure account remains protected by setting permissions.</h6>
        </div>
      </div>
      <form method="post" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4>Set Permissions for {name || "User"}</h4>
              </div>

              <div className="row mt-3 px-3">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Role <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Role"
                        name="code"
                        value={roleCode}
                        onChange={(e) => setRoleCode(e.target.value)}
                      />
                    </div>
                    {errors.code && (
                      <span className="errors text-danger">{errors.code}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table">
                  <thead className="thead-light">
                    <tr>
                      <th>Module</th>
                      {ACTIONS.map((a) => (
                        <th key={a}>{a}</th>
                      ))}
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            modules.length > 0 &&
                            selectedPermissions.length ===
                              modules.length * ACTIONS.length
                          }
                          onChange={(e) => toggleGlobalAll(e.target.checked)}
                        />{" "}
                        Select All
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((module) => (
                      <tr key={module}>
                        <td>{module}</td>

                        {ACTIONS.map((action) => {
                          const key = `${action}_${module}`;
                          return (
                            <td key={action}>
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(key)}
                                onChange={() => togglePermission(module, action)}
                              />
                            </td>
                          );
                        })}

                        <td>
                          <input
                            type="checkbox"
                            checked={ACTIONS.every((a) =>
                              selectedPermissions.includes(`${a}_${module}`),
                            )}
                            onChange={(e) =>
                              toggleModuleAll(module, e.target.checked)
                            }
                          />
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>

        <div className="row mb-3 mt-3">
          <div className="col-12 d-flex justify-content-end">
            <SubmitButton
              isLoading={isLoading}
              text="Submit Details"
              loadingText="Submitting..."
            ></SubmitButton>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Add;